import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Cookie } from 'lucide-react';
import { initAnalytics, disableAnalytics } from '../utils/analytics';

/**
 * RGPD/CNIL-compliant cookie consent banner.
 *
 * Behavior:
 *   - Shown 1.5s after first visit, only if no decision was previously stored.
 *   - "Accepter" → enables analytics + marketing → loads PostHog.
 *   - "Essentiels uniquement" → analytics off → no third-party scripts.
 *   - Stores decision in localStorage to avoid re-prompting.
 *
 * IMPORTANT: this banner is the ONLY place where analytics is enabled.
 * PostHog is NOT auto-loaded from index.html anymore.
 */
export default function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);

    // Load PostHog NOW that consent is given
    initAnalytics();
  };

  const handleAcceptNecessary = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);

    // Make sure analytics is off (in case it was previously enabled)
    disableAnalytics();
  };

  if (!showBanner) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9998] animate-slide-up"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
    >
      <div className="bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-lg">
        <div className="container mx-auto px-4 py-3 md:py-4">
          <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4">
            {/* Icon + Text */}
            <div className="flex items-center gap-3 flex-1">
              <Cookie
                className="w-5 h-5 text-[#635BFF] flex-shrink-0 hidden sm:block"
                aria-hidden="true"
              />
              <div>
                <p id="cookie-banner-title" className="sr-only">
                  Préférences de cookies
                </p>
                <p id="cookie-banner-desc" className="text-sm text-gray-600 text-center sm:text-left">
                  Nous utilisons des cookies pour améliorer votre expérience.{' '}
                  <Link
                    to="/cookie-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#635BFF] hover:underline font-medium"
                  >
                    En savoir plus
                  </Link>
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAcceptNecessary}
                className="border-gray-300 text-gray-600 hover:bg-gray-100 text-xs sm:text-sm px-3"
              >
                Essentiels uniquement
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="bg-[#635BFF] hover:bg-[#5248d9] text-white text-xs sm:text-sm px-3"
              >
                Accepter
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
